export class KeywordExtractor {
  private static readonly STOP_WORDS = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
    'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
    'to', 'was', 'were', 'will', 'with', 'this', 'which', 'or', 'but',
    'have', 'had', 'what', 'when', 'where', 'who', 'why', 'how',
    'all', 'any', 'both', 'each', 'few', 'more', 'most', 'other',
    'some', 'such', 'than', 'too', 'very', 'can', 'cannot', 'could',
    'may', 'might', 'must', 'need', 'ought', 'shall', 'should', 'would',
    'also', 'been', 'being', 'between', 'during', 'having', 'here',
    'there', 'these', 'those', 'into', 'just', 'about', 'above', 'after',
    'before', 'below', 'under', 'again', 'further', 'then', 'once',
    'only', 'same', 'own', 'because', 'until', 'while', 'through',
    'against', 'over', 'out', 'up', 'down', 'off', 'doing', 'does',
    'did', 'not', 'nor', 'your', 'yours', 'his', 'her', 'hers', 'their',
    'theirs', 'our', 'ours', 'them', 'him', 'she', 'they', 'we', 'you',
    'me', 'my', 'i', 'if', 'so', 'no', 'yes', 'yet', 'per', 'via'
  ]);

  // Expanded technical and business terms for government contracting
  private static readonly TECHNICAL_TERMS = new Set([
    // IT & Software
    'software', 'development', 'cloud', 'security', 'network', 'data',
    'analytics', 'infrastructure', 'integration', 'implementation',
    'architecture', 'design', 'testing', 'deployment', 'maintenance',
    'support', 'training', 'consulting', 'management', 'monitoring',
    'automation', 'modernization', 'migration', 'optimization',
    'database', 'storage', 'backup', 'recovery', 'server', 'hosting',
    'cybersecurity', 'encryption', 'firewall', 'compliance', 'audit',
    'assessment', 'analysis', 'reporting', 'visualization', 'ai', 'ml',
    'artificial', 'intelligence', 'machine', 'learning', 'blockchain',
    'iot', 'internet', 'things', 'mobile', 'web', 'api', 'service',
    'platform', 'solution', 'system', 'application', 'program', 'code',
    'agile', 'devops', 'cicd', 'continuous', 'delivery', 'saas', 'paas',
    'iaas', 'azure', 'aws', 'gcp', 'kubernetes', 'docker', 'container',
    'microservices', 'serverless', 'virtual', 'virtualization', 'vmware',
    'linux', 'windows', 'unix', 'oracle', 'sql', 'nosql', 'mongodb',
    'postgresql', 'mysql', 'redis', 'elasticsearch', 'hadoop', 'spark',
    'python', 'java', 'javascript', 'typescript', 'react', 'angular',
    'vue', 'node', 'dotnet', 'csharp', 'golang', 'rust', 'scala',
    'terraform', 'ansible', 'puppet', 'chef', 'jenkins', 'gitlab',
    'github', 'bitbucket', 'jira', 'confluence', 'slack', 'teams',

    // Healthcare & Medical
    'healthcare', 'medical', 'clinical', 'patient', 'electronic',
    'records', 'portal', 'ehr', 'emr', 'hipaa', 'health', 'hospital',
    'pharmacy', 'pharmaceutical', 'diagnostic', 'imaging', 'laboratory',
    'telemedicine', 'telehealth', 'wellness', 'insurance', 'claims',
    'billing', 'coding', 'icd', 'cpt', 'fhir', 'hl7', 'dicom',

    // Defense & Security
    'defense', 'military', 'army', 'navy', 'force', 'marine', 'coast',
    'guard', 'dod', 'pentagon', 'intelligence', 'surveillance',
    'reconnaissance', 'isr', 'weapons', 'missile', 'aircraft', 'ship',
    'vehicle', 'tactical', 'strategic', 'operational', 'combat',
    'logistics', 'supply', 'chain', 'ammunition', 'ordnance', 'radar',
    'sonar', 'satellite', 'communications', 'c4isr', 'cyber', 'space',
    'clearance', 'secret', 'classified', 'sensitive', 'sbu', 'fouo',
    'nist', 'fisma', 'fedramp', 'cmmc', 'dfars', 'itar', 'ear',

    // Engineering & Construction
    'engineering', 'construction', 'building', 'facility', 'facilities',
    'hvac', 'electrical', 'mechanical', 'plumbing', 'structural',
    'civil', 'environmental', 'geotechnical', 'surveying', 'drafting',
    'cad', 'bim', 'revit', 'autocad', 'gis', 'mapping', 'architecture',
    'renovation', 'repair', 'maintenance', 'inspection', 'quality',
    'safety', 'osha', 'contractor', 'subcontractor', 'blueprint',

    // Professional Services
    'professional', 'services', 'advisory', 'strategy', 'strategic',
    'planning', 'program', 'project', 'portfolio', 'pmo', 'governance',
    'policy', 'procedure', 'process', 'improvement', 'reengineering',
    'transformation', 'change', 'organizational', 'workforce', 'hr',
    'human', 'resources', 'capital', 'talent', 'recruitment', 'staffing',
    'acquisition', 'procurement', 'contracting', 'grants', 'financial',
    'accounting', 'budgeting', 'forecasting', 'audit', 'internal',
    'controls', 'risk', 'enterprise', 'erp', 'sap', 'oracle',

    // Research & Development
    'research', 'development', 'rnd', 'innovation', 'prototype',
    'testing', 'evaluation', 'laboratory', 'lab', 'experiment',
    'scientific', 'technical', 'study', 'analysis', 'modeling',
    'simulation', 'sbir', 'sttr', 'grant', 'cooperative', 'agreement',

    // Operations & Maintenance
    'operations', 'maintenance', 'sustainment', 'lifecycle', 'asset',
    'equipment', 'fleet', 'vehicle', 'depot', 'field', 'technical',
    'repair', 'overhaul', 'calibration', 'warranty', 'parts', 'spare',
    'inventory', 'warehouse', 'distribution', 'transportation',

    // Administrative & Support
    'administrative', 'clerical', 'secretarial', 'receptionist',
    'customer', 'call', 'center', 'helpdesk', 'ticketing', 'document',
    'records', 'scanning', 'digitization', 'mailroom', 'printing',
    'copying', 'shipping', 'receiving', 'courier', 'messenger',

    // Environmental & Energy
    'environmental', 'sustainability', 'renewable', 'solar', 'wind',
    'energy', 'efficiency', 'conservation', 'recycling', 'waste',
    'hazardous', 'remediation', 'cleanup', 'pollution', 'air', 'water',
    'soil', 'groundwater', 'contamination', 'nepa', 'epa', 'compliance',

    // Training & Education
    'training', 'education', 'curriculum', 'instruction', 'instructor',
    'course', 'certification', 'accreditation', 'elearning', 'lms',
    'simulation', 'exercise', 'workshop', 'seminar', 'conference',

    // Communications & Media
    'communications', 'marketing', 'public', 'affairs', 'relations',
    'media', 'social', 'digital', 'content', 'creative', 'graphic',
    'video', 'audio', 'production', 'photography', 'writing', 'editing',
    'translation', 'interpretation', 'language', 'outreach', 'engagement',

    // Specialized Government Terms
    'federal', 'government', 'agency', 'department', 'bureau', 'office',
    'division', 'branch', 'section', 'unit', 'directorate', 'command',
    'base', 'installation', 'post', 'station', 'center', 'mission',
    'objective', 'requirement', 'specification', 'statement', 'work',
    'performance', 'deliverable', 'milestone', 'schedule', 'cost',
    'price', 'proposal', 'solicitation', 'rfp', 'rfq', 'rfi', 'bpa',
    'idiq', 'gwac', 'mac', 'bic', 'gsa', 'schedule', 'task', 'order',
    'contract', 'award', 'modification', 'option', 'period', 'base',
    'transition', 'incumbent', 'offeror', 'vendor', 'supplier', 'prime',
    'subcontractor', 'teaming', 'joint', 'venture', 'mentor', 'protege',

    // Certifications & Standards
    'iso', 'cmmi', 'itil', 'pmp', 'cissp', 'cism', 'cisa', 'ccna',
    'ccnp', 'comptia', 'security', 'certified', 'certification', 'sox',
    'gdpr', 'pci', 'dss', 'aicpa', 'gaap', 'fas', 'far', 'dfars',
    'section', '508', 'ada', 'accessibility', 'wcag',

    // Emerging Technologies
    'quantum', 'computing', 'augmented', 'reality', 'ar', 'vr', 'mixed',
    'mr', 'robotics', 'autonomous', 'drone', 'uav', 'uas', 'sensor',
    'biometrics', 'facial', 'recognition', 'nlp', 'natural', 'processing',
    'edge', '5g', 'wireless', 'spectrum', 'mesh', 'distributed',
    'ledger', 'defi', 'nft', 'metaverse', 'digital', 'twin'
  ]);

  // Government agency acronyms and common abbreviations
  private static readonly AGENCY_TERMS = new Set([
    'dod', 'dhs', 'hhs', 'va', 'ssa', 'gsa', 'nasa', 'faa', 'fbi',
    'cia', 'nsa', 'dia', 'nga', 'nro', 'disa', 'dcma', 'dla', 'usda',
    'doe', 'dot', 'doj', 'dos', 'doi', 'doc', 'dol', 'hud', 'ed',
    'epa', 'sba', 'opm', 'gao', 'omb', 'cio', 'ciso', 'cto', 'coo',
    'usace', 'navfac', 'afcec', 'imcom', 'spawar', 'navsea', 'navsup',
    'aflcmc', 'acc', 'peo', 'acat', 'jcids', 'ppbe', 'dau', 'dtic'
  ]);

  static extractKeywords(text: string): string[] {
    if (!text) return [];

    // Convert to lowercase and clean up text
    const cleanedText = text.toLowerCase()
      .replace(/[^\w\s-]/g, ' ') // Keep hyphens for compound words
      .replace(/\s+/g, ' ')      // Normalize whitespace
      .trim();

    const words = cleanedText.split(' ')
      .filter(word =>
        word.length > 2 &&
        !this.STOP_WORDS.has(word)
      );

    // Filter for relevant terms (technical terms, agency terms, or numbers)
    const relevantWords = words.filter(word =>
      this.TECHNICAL_TERMS.has(word) ||
      this.AGENCY_TERMS.has(word) ||
      /\d/.test(word) ||
      word.length > 5 // Include longer words that might be relevant
    );

    // Generate compound terms (2-3 words) for phrases
    const compounds: string[] = [];
    for (let i = 0; i < words.length - 1; i++) {
      const twoWords = `${words[i]} ${words[i + 1]}`;
      compounds.push(twoWords);

      if (i < words.length - 2) {
        const threeWords = `${words[i]} ${words[i + 1]} ${words[i + 2]}`;
        compounds.push(threeWords);
      }
    }

    // Combine single words and compounds
    const allTerms = [...relevantWords, ...compounds];

    // Remove duplicates and sort by length (longer terms first for better matching)
    return Array.from(new Set(allTerms))
      .sort((a, b) => b.length - a.length)
      .slice(0, 50); // Keep top 50 most relevant terms
  }

  static calculateSimilarity(keywords1: string[], keywords2: string[]): number {
    if (!keywords1.length || !keywords2.length) return 0;

    // Normalize all keywords
    const normalize = (str: string) => str.toLowerCase().trim();

    const set1 = new Set(keywords1.map(normalize));
    const set2 = new Set(keywords2.map(normalize));

    // Calculate exact matches
    let exactMatchScore = 0;
    const exactMatches = new Set<string>();

    for (const term of set1) {
      if (set2.has(term)) {
        exactMatches.add(term);
        // Weight longer matches more heavily
        exactMatchScore += term.split(' ').length;
      }
    }

    // Calculate partial matches (substring matching)
    let partialMatchScore = 0;
    const partialMatches = new Set<string>();

    for (const term1 of set1) {
      if (exactMatches.has(term1)) continue;

      for (const term2 of set2) {
        if (exactMatches.has(term2)) continue;

        // Check for word overlap in compound terms
        const words1 = term1.split(' ');
        const words2 = term2.split(' ');

        for (const w1 of words1) {
          for (const w2 of words2) {
            if (w1.length > 3 && w2.length > 3) {
              if (w1.includes(w2) || w2.includes(w1)) {
                partialMatches.add(`${term1}|${term2}`);
                partialMatchScore += 0.5;
              }
            }
          }
        }
      }
    }

    // Calculate similarity score
    // Exact matches count as 2 points each, partial as 0.5
    const totalMatchScore = exactMatchScore * 2 + partialMatchScore;
    const maxPossibleScore = Math.max(set1.size, set2.size) * 2;

    const similarity = maxPossibleScore > 0 ? totalMatchScore / maxPossibleScore : 0;

    // Cap at 1.0 and apply a slight boost for having any matches
    return Math.min(1, similarity * (exactMatches.size > 0 ? 1.2 : 1));
  }

  // Extract key phrases from text (useful for displaying matched terms)
  static extractMatchedTerms(text: string, profileKeywords: string[]): string[] {
    const textKeywords = this.extractKeywords(text);
    const normalizedProfile = new Set(profileKeywords.map(k => k.toLowerCase()));

    const matched: string[] = [];

    for (const keyword of textKeywords) {
      const normalized = keyword.toLowerCase();
      if (normalizedProfile.has(normalized)) {
        matched.push(keyword);
      } else {
        // Check for partial matches
        for (const profileWord of normalizedProfile) {
          if (normalized.includes(profileWord) || profileWord.includes(normalized)) {
            matched.push(keyword);
            break;
          }
        }
      }
    }

    return [...new Set(matched)].slice(0, 10);
  }
}
